<template>
  <modal-vue
    @close="close"
    :modalActive="modalStateProp"
    :hasFooterBorder="true"
    :hasHeaderBorder="true"
    maxWidth="max-w-lg"
  >
    <template #header>
      <p class="font-bold text-start">Criar convite</p>
    </template>

    <div class="flex flex-col gap-4">
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="sm:flex-1">
          <GenericInput
            v-model="form.crm"
            label="CRM"
            width="100%"
            :error-messages="errors.crm"
            data-testid="invite-crm-input"
            @update:model-value="clearError('crm')"
          />
        </div>

        <div class="w-full sm:w-40">
          <v-autocomplete
            v-model="form.crmUf"
            label="CRMUF"
            :items="StatesFromBR"
            placeholder="Selecione"
            variant="outlined"
            :error-messages="errors.crmUf"
            data-testid="invite-crmuf-select"
            @update:model-value="clearError('crmUf')"
          />
        </div>
      </div>

      <v-autocomplete
        v-model="form.companyIds"
        color="primary20"
        label="Empresas"
        :items="listCompanies"
        variant="outlined"
        multiple
        chips
        :error-messages="errors.companyIds"
        data-testid="invite-company-select"
        @update:model-value="clearError('companyIds')"
      />

      <!-- 409 do guardrail de identidade: a pessoa já tem cadastro por outro CRM -->
      <div
        v-if="crmConflict"
        data-testid="invite-crm-conflict"
        class="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-start"
      >
        <p class="text-gray-800">
          O CRM {{ crmConflict.requestedCrm.number }}/{{ crmConflict.requestedCrm.uf }} pertence a
          <span class="font-medium">{{ crmConflict.cfmName }}</span
          >, que já possui cadastro (CRM {{ crmConflict.existingCrm.number }}/{{
            crmConflict.existingCrm.uf
          }}). A transferência de CRM é feita pelo suporte — abra um chamado no Help Desk em vez de
          um novo convite.
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex flex-col md:flex-row md:justify-end gap-2">
        <div class="w-full md:max-w-[140px] order-2 md:order-1">
          <OutlineButton
            @click="close"
            text="Cancelar"
            width="100%"
            data-testid="invite-cancel-button"
          />
        </div>
        <div class="w-full md:max-w-[140px] order-1 md:order-2">
          <filled-button
            :loading="loading"
            @click="submitCreateInvite"
            text="Criar convite"
            width="100%"
            data-testid="invite-submit-button"
          />
        </div>
      </div>
    </template>
  </modal-vue>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import StatesFromBR from '@/config/StatesFromBR'
import GenericInput from '@/components/designSystem/Inputs/GenericInput.vue'
import { storeToRefs } from 'pinia'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import Network from '@/Network'
import { ValidationError } from 'yup'
import { createInviteSchema } from './schemas'
import {
  getInviteCrmConflict,
  getInviteErrorMessage,
  type InviteCrmConflict
} from '../../helpers/inviteErrors'

import { useToast } from 'vue-toastification'

const props = defineProps<{
  modalStateProp: boolean
  updateListFunction?: () => Promise<void> | void
}>()

const emit = defineEmits(['close'])

const form = reactive({
  companyIds: [] as (string | number)[],
  crm: '',
  crmUf: '',
  autoApprove: false
})

const errors = reactive<{ crm?: string; crmUf?: string; companyIds?: string }>({})

const loading = ref(false)
const crmConflict = ref<InviteCrmConflict | null>(null)
const { listCompanies } = storeToRefs(useCompanyStore())
const toast = useToast()

const clearError = (field: keyof typeof errors) => {
  errors[field] = undefined
  crmConflict.value = null
}

const validateForm = async () => {
  errors.crm = undefined
  errors.crmUf = undefined
  errors.companyIds = undefined

  try {
    await createInviteSchema.validate(form, { abortEarly: false })
    return true
  } catch (validationError) {
    if (validationError instanceof ValidationError) {
      validationError.inner.forEach((issue) => {
        const field = issue.path as keyof typeof errors
        if (field && !errors[field]) {
          errors[field] = issue.message
        }
      })
    }
    return false
  }
}

const submitCreateInvite = async () => {
  if (!(await validateForm())) return

  try {
    loading.value = true
    const sanitizedCrm = form.crm.replace(/\s+/g, '')
    const numericCompanyIds = form.companyIds.map((id) => Number(id))

    await Network.company.inviteDoctorForCompany({
      crm: sanitizedCrm,
      crmUf: form.crmUf,
      autoApprove: form.autoApprove,
      companyIds: numericCompanyIds
    })

    toast.success('Médico(s) vinculado(s) com sucesso')
    close()

    if (props.updateListFunction) {
      try {
        await props.updateListFunction()
      } catch (error) {
        // fallback intencional: o convite já foi criado (toast de sucesso acima) — um
        // toast de erro aqui pareceria falha do convite. A lista vem de
        // `doctorStore.fetchDoctors`, que não relança: a falha aparece no estado
        // `isError` da tela de médicos (Doctors/Index.vue).
        console.error('Erro ao atualizar a lista:', error)
      }
    }
  } catch (error) {
    // 409 do guardrail: alerta inline orientando o chamado no Help Desk, sem toast
    const conflict = getInviteCrmConflict(error)
    if (conflict) {
      crmConflict.value = conflict
      return
    }
    // Guard de domínio (convite já ativo → mensagem curada): exibido direto,
    // pois como fallback de notifyError a mensagem curada nunca apareceria.
    toast.error(
      getInviteErrorMessage(
        error,
        'Não foi possível vincular o médico. Verifique os dados e tente novamente.'
      )
    )
    console.error('Erro ao criar convite:', error)
  } finally {
    loading.value = false
  }
}

const clear = () => {
  form.crm = ''
  form.crmUf = ''
  form.companyIds = []
  errors.crm = undefined
  errors.crmUf = undefined
  errors.companyIds = undefined
  crmConflict.value = null
}

const close = () => {
  clear()
  emit('close')
}
</script>
